import {useSyncExternalStore} from 'react';
import {MenuItem, Select, TableCell, Typography} from '@mui/material';
import {Page} from '../../components/page/Page';
import {DataTable} from '../../components/data-table/DataTable';
import {TableColumnData} from '../../components/data-table/TableColumnData';
import {DEFAULT_LOG_LEVEL, LogLevel, loggerRegistry, LoggerSnapshot} from '../../util/Logger';

type LoggerEntry = LoggerSnapshot[number];

const LOG_LEVEL_OPTIONS = Object.entries(LogLevel)
    .filter(([, v]) => typeof v === 'number')
    .map(([label, value]) => ({label, value: value as LogLevel}));

function LevelCell({entry}: { entry: LoggerEntry }) {
    return (
        <TableCell>
            <Select
                size="small"
                value={entry.level}
                onChange={e => loggerRegistry.setLevel(entry.name, e.target.value as LogLevel)}
            >
                {LOG_LEVEL_OPTIONS.map(({label, value}) => (
                    <MenuItem key={label} value={value}>
                        {value === DEFAULT_LOG_LEVEL ? `${label} (default)` : label}
                    </MenuItem>
                ))}
            </Select>
        </TableCell>
    );
}

const columns: TableColumnData<LoggerEntry>[] = [
    {
        key: 'name',
        header: 'Logger',
        cell: entry => <TableCell><Typography variant="body2" fontFamily="monospace">{entry.name}</Typography></TableCell>,
    },
    {
        key: 'level',
        header: 'Level',
        width: '16em',
        cell: entry => <LevelCell entry={entry}/>,
    },
];

export default function LoggersPage() {
    const entries = useSyncExternalStore(
        listener => loggerRegistry.subscribe(listener),
        () => loggerRegistry.getSnapshot(),
    );

    return (
        <Page
            header="Loggers"
            counter={<Typography variant="caption" color="textSecondary">{entries.length}</Typography>}
            description="Configure log levels for in-browser loggers. Changes are saved to localStorage and apply immediately."
        >
            <DataTable
                columns={columns}
                rows={entries}
                id={entry => entry.name}
            />
        </Page>
    );
}
